import {useCallback, useMemo} from 'react';
import {useMutation} from '@apollo/client';
import {Divider, Menu, Box} from '@mui/material';
import {grey} from '@mui/material/colors';
import moment from 'moment-timezone';
import {NestedMenuItem} from 'mui-nested-menu';
import {useNavigate} from 'react-router-dom';

import {
  CLEAR_SESSIONS,
  CREATE_SESSION,
  DELETE_SESSION,
  UPDATE_SESSION_LOCK_STATUS,
  UPDATE_SHIFT_BLOCK_STATUS
} from '../../queries/GraphQLQueries';
import {buildServiceTypeDetails} from '../../helpers/ServiceTypeUtils';
import {SESSION_TYPE} from '../../domain/SessionType';
import {DEFAULT_TIMESLOT_LENGTH_IN_SECONDS, determineShiftQueryName, getClearSessionsAlertMessage} from '../../helpers/ShiftHelper';
import {buildDeletableSessionIds} from '../../helpers/SessionHelper';
import {NAVIGATION_PATHS} from '../../config/NavigationPaths';
import ContextMenuItem from './ContextMenuItem';
import {ScheduleViewTypes} from '../../domain/ScheduleViewTypes';

const styles = {
  menuList: {
    padding: '0rem'
  },
  menuItemBox: {
    my: '-0.5rem'
  },
  nestedMenuItem: {
    '&.Mui-focusVisible': {backgroundColor: 'transparent'},
    '&:hover': {backgroundColor: grey[100]}
  },
  nestedMenuItemWithPadding: {
    padding: '0.5rem'
  }
};

const buildAlertText = (loadingAction, successAction, label) => ({
    loadingMessage: (count) => `${loadingAction} ${count} ${label}(s)...`,
    successMessage: (count) => `Successfully ${successAction} ${count} ${label}(s)`
});

const NON_TEACHING_SESSION_ALERT_MESSAGES = {
    [SESSION_TYPE.BLOCKED]: buildAlertText("Blocking", "blocked", "slot"),
    [SESSION_TYPE.BREAK]: buildAlertText("Marking", "created", "break session"),
    [SESSION_TYPE.TRAINING]: buildAlertText("Marking", "created", "training session")
};

export const SESSION_LABEL = {
    BLOCKED: 'Blocked',
    BREAK: 'Break',
    TRAINING: 'Training',
    UNBLOCK: 'Unblock'
};

const TimeslotContextMenu = (props) => {

  const MAX_SESSIONS_ALLOWED_TO_CREATE = 8;
  const SESSION_MENU_OPTIONS = [1, 2, 4, 8];

  const SESSION_KEY = {
    BLOCKED: 'blocked',
    BREAK: 'break',
    TRAINING: 'training',
    UNBLOCK: 'unblock'
  };
  const {contextMenu, setContextMenu, setAlertMessage, shiftData, event} = props;

  const navigate = useNavigate();

  const refetchShiftQueriesForView = {refetchQueries: [determineShiftQueryName(ScheduleViewTypes.DETAILED)]};
  const [createSession] = useMutation(CREATE_SESSION, refetchShiftQueriesForView);
  const [deleteSession] = useMutation(DELETE_SESSION, refetchShiftQueriesForView);
  const [modifyShiftBlockedStatus] = useMutation(UPDATE_SHIFT_BLOCK_STATUS, refetchShiftQueriesForView);
  const [clearSessions] = useMutation(CLEAR_SESSIONS, refetchShiftQueriesForView);
  const [updateSessionLockStatus] = useMutation(UPDATE_SESSION_LOCK_STATUS, refetchShiftQueriesForView);

  const handleContextMenuClose = () => setContextMenu(null);

  const getSwappableSessions = (sourceSessionToSwap, sessionDetails) => {
    return sessionDetails.filter(session => {
      return (session.from === sourceSessionToSwap.from
        && session.owner !== sourceSessionToSwap.owner
        && session.serviceType.keycode === sourceSessionToSwap.serviceType.keycode);
    }).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));
  const isShiftComplete = moment(event.shiftEndTime).isBefore();
  const isSessionComplete = moment(event.to).isAfter();
  const isBreakSession = event.session?.sessionType === SESSION_TYPE.BREAK;
  const isTrainingSession = event.session?.sessionType === SESSION_TYPE.TRAINING;
  const isBlockedSession = event.session?.sessionType === SESSION_TYPE.BLOCKED;
  const canCreateSession = isSessionComplete && !event.hasSession && !isBreakSession;
  const canLockSession = isSessionComplete && event.hasSession && !isBreakSession;
  const canBlockTimeRange = isSessionComplete && !event.hasSession;
  const canMakeBooking = isSessionComplete && event.hasSession;
  const canViewSessionMenuOptions = isSessionComplete && event.hasSession && !isBreakSession;
  const canDeleteSession = !event.isBooked;
  const canDeleteShift = event.isAnySessionAvailableInTheShift;
  const canClearSessions = !isShiftComplete && event.isAnySessionAvailableInTheShift;
  const canSwapSession = enableSwapSessionMenu(event) && !isBreakSession;
  const hasSession = event.hasSession;
  const isSubMenuOpen = !!contextMenu?.clickedItemId && contextMenu.clickedItemId == event.id;

  const buildAvailableSessionCount = useCallback((excludeBreakSession = true) => {

    let consecutiveEmptyTimeslotsCount = 0;

    if (!event?.from || !event?.to || !isSessionComplete || hasSession || (excludeBreakSession && isBreakSession)) {
      return consecutiveEmptyTimeslotsCount;
    }

    const shiftEndTime = moment.utc(event.shiftEndTime || event.to.toISOString());
    let currentTime = moment.utc(event.from.toISOString());

    const currentShiftData = shiftData.filter(item => item.shiftId === event.availabilityId);

    while (currentTime.isBefore(shiftEndTime)) {
      const sessionEndTime = moment.utc(currentTime).add(DEFAULT_TIMESLOT_LENGTH_IN_SECONDS, 'seconds');

      if (sessionEndTime.isAfter(shiftEndTime)) {
        break;
      }

      const isSessionOccupied = currentShiftData.some(item => {
        if (item.id === event.id) {
          return false;
        }

        if (!item.hasSession) {
          return false;
        }

        const itemStart = moment.utc(item.from.toISOString());
        const itemEnd = moment.utc(item.to.toISOString());

        return (
          (currentTime.isSameOrAfter(itemStart) && currentTime.isBefore(itemEnd)) ||
          (sessionEndTime.isAfter(itemStart) && sessionEndTime.isSameOrBefore(itemEnd)) ||
          (currentTime.isSameOrBefore(itemStart) && sessionEndTime.isSameOrAfter(itemEnd))
        );
      });

      if (isSessionOccupied) {
        break;
      }

      consecutiveEmptyTimeslotsCount++;
      currentTime.add(DEFAULT_TIMESLOT_LENGTH_IN_SECONDS, 'seconds');
    }

    return consecutiveEmptyTimeslotsCount;
  }, [event, shiftData, hasSession, isSessionComplete, isBreakSession]);

  const memoisedBreakSessionCount = useMemo(() => buildAvailableSessionCount(true), [buildAvailableSessionCount]);

  const memoisedTrainingSessionCount = useMemo(() => buildAvailableSessionCount(false), [buildAvailableSessionCount]);

  const memoisedBlockedSessionCount = useMemo(() => buildAvailableSessionCount(false), [buildAvailableSessionCount]);
  
  const memoisedDeletableBlockedSessionIds = useMemo(() => buildDeletableSessionIds(event, shiftData, isSessionComplete), [event, shiftData, isSessionComplete]);

  const createNonTeachingSession = useCallback((sessionCount, sessionType) => {
    const alertMessages = NON_TEACHING_SESSION_ALERT_MESSAGES[sessionType];

    setAlertMessage({ children: alertMessages.loadingMessage(sessionCount), severity: 'success' });
    setContextMenu(null);
  
    const startTime = moment.utc(event.from.toISOString());
    const endTime = moment.utc(event.from.toISOString()).add(sessionCount * DEFAULT_TIMESLOT_LENGTH_IN_SECONDS, 'seconds');

    createSession({
      variables: {
        shiftId: event.availabilityId,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        sessionType: sessionType
      }
    })
    .then(() => {
      setAlertMessage({ children: alertMessages.successMessage(sessionCount), severity: 'success' });
    })
    .catch(error => {
      setAlertMessage({ children: error?.message, severity: 'error' });
      console.error(error);
    });

  }, [event.availabilityId, event.from, createSession]);

  const setAsBlockedSession = useCallback((count) => createNonTeachingSession(count, SESSION_TYPE.BLOCKED), [createNonTeachingSession]);
  const setAsBreakSession = useCallback((count) => createNonTeachingSession(count, SESSION_TYPE.BREAK), [createNonTeachingSession]);
  const setAsTrainingSession = useCallback((count) => createNonTeachingSession(count, SESSION_TYPE.TRAINING), [createNonTeachingSession]);

  const deleteSelectedSessionsToUnblockTimeblock = useCallback((sessionCountToDelete) => {
    setAlertMessage({children: `Deleting ${sessionCountToDelete} blocked session(s) to unblock timeblock...`, severity: 'success'});
    setContextMenu(null);

    const blockedSessionIdsToDelete = memoisedDeletableBlockedSessionIds.slice(0, sessionCountToDelete);

    const deleteSelectedBlockedSessions = blockedSessionIdsToDelete.map(sessionId => deleteSession({variables: {lessonId: sessionId}}));

    Promise.all(deleteSelectedBlockedSessions)
        .catch(error => {setAlertMessage({children: error?.message, severity: 'error'});
          console.error(error);
        });

  }, [memoisedDeletableBlockedSessionIds, deleteSession]);

  const handleDeleteShift = useCallback(() => {
    setContextMenu(null);
    navigate(NAVIGATION_PATHS.DELETE_SHIFT, {
      state: {
          shiftToDelete: deepCopy(event)
      }
    });
  }, [event, navigate, deepCopy]);

  const handleShiftBlockStatusUpdate = (isBlocked) => {
    const requiredBookabilityStatus = isBlocked ? 'blocked' : 'unblocked';
    setAlertMessage({ children: `Changing shift bookability to ${requiredBookabilityStatus}...`, severity: 'success' });
    setContextMenu(null);

    modifyShiftBlockedStatus({
      variables: {
        shiftId: event.availabilityId,
        isBlocked: isBlocked,
      }
    }).then((response) => {

      const modifiedSessionCount = response.data.updateShiftBookability;
      const areSomeOrAllSessionsAlreadyInRequestedStatus = (modifiedSessionCount === 0 || modifiedSessionCount < event.availabilitySessionCount);

      let message = `Changed shift bookability to ${requiredBookabilityStatus}`;
      let severity = 'success';

      if (event.completedSessionCount > 0) {
        message = `Not all timeslots were changed to ${requiredBookabilityStatus} as they’re in the past`;
        severity = 'warning';
      } else if (areSomeOrAllSessionsAlreadyInRequestedStatus) {
        message = `Not all timeslots were changed, as they’re already ${requiredBookabilityStatus}`;
        severity = 'warning';
      }

      setAlertMessage({ children: message, severity: severity });
    }).catch(error => {
      setAlertMessage({ children: error?.message, severity: 'error' });
      console.log(error);
    });
  }

  const handleClearSessions = () => {
    setAlertMessage({ children: `Clearing sessions...`, severity: 'success' });
    setContextMenu(null);

    clearSessions({
      variables: {
        shiftId: event.availabilityId
      }
    }).then((response) => {

      const { totalSessionsCount, successfullyClearedSessionsCount } = response.data.clearSessions;

      const { severity, message } = getClearSessionsAlertMessage(
        totalSessionsCount,
        successfullyClearedSessionsCount
      );

      setAlertMessage({ children: message, severity });

    }).catch(error => {
      setAlertMessage({ children: error?.message || 'Failed to clear session.', severity: 'error' });
      console.error(error);
    });
  };

  function handleCreateSession() {
    const sessionId = event.session.id;
    setContextMenu(null);

    navigate(NAVIGATION_PATHS.CREATE_SESSION, {
      state: {
        shiftId: event.availabilityId,
        sessionStartTime: event.from.toISOString(),
        sessionId: sessionId,
        performer: event.displayName,
        serviceType: buildServiceTypeDetails(event)
      }
    });
  }

  function handleMakeBooking() {
    setContextMenu(null);

    navigate(NAVIGATION_PATHS.SHIFT_BOOKING, {
      state: {
        performer: event.displayName,
        timeslotTime: event.from.toISOString(),
        serviceTypeDescription: event.session.serviceType.description,
        content: event.session.contents,
        lessonId: event.session.id
      }
    });
  }

  function handleDeleteTeachingSession() {
    setAlertMessage({ children: 'Deleting Session...', severity: 'success' });
    setContextMenu(null);

    deleteSession({variables: {lessonId: event.session.id}})
    .then(() => {
      setAlertMessage({children: 'Session Deleted', severity: 'success'})
    }).catch(error => {
      setAlertMessage({children: error?.message, severity: 'error'});
      console.log(error);
    });
  }

  function enableSwapSessionMenu() {
    if (isSessionComplete && event.hasSession) {
      const sourceSessionToSwap = deepCopy(event);
      const targetSessions = getSwappableSessions(sourceSessionToSwap, deepCopy(shiftData));
      return !!targetSessions && targetSessions.length > 0;
    } else {
      return false;
    }
  }

  function handleSwapSession() {
    const sourceSessionToSwap = deepCopy(event);

    setContextMenu(null);
    navigate(NAVIGATION_PATHS.SWAP_SESSION, {
      state: {
        sourceSessionToSwap: sourceSessionToSwap
      }
    });
  }

  const handleEditShift = () => {
    setContextMenu(null);
    const shiftToEdit = deepCopy(event);
    navigate(NAVIGATION_PATHS.EDIT_SHIFT, {
      state: {
        shiftInfo: shiftToEdit,
        isEditMode: true
      }
    });
  }

  const handleSessionLockToggle = (isLocked) => {
    const action = isLocked ? 'Locking' : 'Unlocking';
    const successMessage = isLocked ? 'Session Locked' : 'Session Unlocked';

    setAlertMessage({children: `${action} Session...`, severity: 'success'});
    setContextMenu(null);

    updateSessionLockStatus({variables: {sessionId: event.session.id, isLocked}})
    .then(() => {
        setAlertMessage({children: successMessage, severity: 'success'});
    })
    .catch(error => {
        setAlertMessage({children: error?.message, severity: 'error'});
        console.log(error);
    });
  }

  const renderSessionOptions = (sessionCount, onClickHandler, sessionType, keyPrefix) => {
    if (sessionCount <= 0) {
      return <ContextMenuItem disabled label={`No available ${sessionType.toLowerCase()} sessions`} />;
    }

    const contextMenuItems = [];
    const maxAvailableOptionCount = Math.min(sessionCount, MAX_SESSIONS_ALLOWED_TO_CREATE);
    const countValues = SESSION_MENU_OPTIONS.filter(val => val <= maxAvailableOptionCount);

    countValues.forEach((count, index) => {

      if (index > 0){
        contextMenuItems.push(<Divider key={`div-${keyPrefix}-${count}`} />);
      }

      contextMenuItems.push(
        <ContextMenuItem
          key={`${keyPrefix}-${index}`}
          onClick={() => onClickHandler(count)}
          label={`${count} ${sessionType} Slot${count > 1 ? 's' : ''}`} />
      );
    });

    return contextMenuItems;
  };

  const renderCreateBlockedSessionOptions = () => {
    return renderSessionOptions(memoisedBlockedSessionCount, setAsBlockedSession, SESSION_LABEL.BLOCKED, SESSION_KEY.BLOCKED);
  };

  const renderCreateBreakSessionOptions = () => {
    return renderSessionOptions(memoisedBreakSessionCount, setAsBreakSession, SESSION_LABEL.BREAK, SESSION_KEY.BREAK);
  };

  const renderCreateTrainingSessionOptions = () => {
    return renderSessionOptions(memoisedTrainingSessionCount, setAsTrainingSession, SESSION_LABEL.TRAINING, SESSION_KEY.TRAINING);
  }

  const renderDeletableSessionOptions = () => {
    return renderSessionOptions(memoisedDeletableBlockedSessionIds.length, deleteSelectedSessionsToUnblockTimeblock, SESSION_LABEL.UNBLOCK, SESSION_KEY.UNBLOCK);
  };

  return(
    <Menu
      open={contextMenu?.clickedItemId && contextMenu.clickedItemId == event.id}
      onClose={handleContextMenuClose}
      anchorReference="anchorPosition"
      MenuListProps={{sx: styles.menuList}}
      anchorPosition={
      contextMenu !== null
        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
        : undefined
    }>
      <NestedMenuItem label="Timeslot" sx={styles.nestedMenuItem} parentMenuOpen={isSubMenuOpen}>
        <Box sx={styles.menuItemBox}>
          <NestedMenuItem label="Set Break" sx={styles.nestedMenuItemWithPadding} parentMenuOpen={isSubMenuOpen} disabled={hasSession || !isSessionComplete || isBreakSession} >
            <Box sx={styles.menuItemBox}>
              {renderCreateBreakSessionOptions()}
            </Box>
          </NestedMenuItem>
          <NestedMenuItem label="Set Training" sx={styles.nestedMenuItemWithPadding} parentMenuOpen={isSubMenuOpen} disabled={hasSession || !isSessionComplete || isTrainingSession} >
            <Box sx={styles.menuItemBox}>
              {renderCreateTrainingSessionOptions()}
            </Box>
          </NestedMenuItem>
          {!!isSessionComplete && <Divider/>}
          <NestedMenuItem label="Set Blocked Slot" sx={styles.nestedMenuItemWithPadding} parentMenuOpen={isSubMenuOpen} disabled={hasSession || !canBlockTimeRange || isBlockedSession}>
            <Box sx={styles.menuItemBox}>
              {renderCreateBlockedSessionOptions()}
            </Box>
          </NestedMenuItem>
          <NestedMenuItem label="Unblock Slot(s)" sx={styles.nestedMenuItemWithPadding} parentMenuOpen={isSubMenuOpen} disabled={!(isBlockedSession || isBreakSession || isTrainingSession)}>
            <Box sx={styles.menuItemBox}>
              {renderDeletableSessionOptions()}
            </Box>
          </NestedMenuItem>
        </Box>
      </NestedMenuItem>
      <Divider />
      <NestedMenuItem label="Session" sx={styles.nestedMenuItem} parentMenuOpen={isSubMenuOpen}>
        {<Box sx={styles.menuItemBox}>
          <ContextMenuItem onClick={handleSwapSession} disabled={!canSwapSession} label="Swap Session..." />
          <ContextMenuItem onClick={() => handleSessionLockToggle(true)} disabled={!canLockSession} label="Lock Session" />
          <ContextMenuItem onClick={() => handleSessionLockToggle(false)} disabled={!canLockSession} label="Unlock Session" />
          <Divider />
          <ContextMenuItem onClick={handleCreateSession} disabled={!canCreateSession} label="Create Session..." />
          {canViewSessionMenuOptions && <Divider />}
          {canViewSessionMenuOptions && <ContextMenuItem onClick={handleDeleteTeachingSession} disabled={!canDeleteSession} label="Delete Session" />}
        </Box>}
      </NestedMenuItem>
      <Divider />
      <NestedMenuItem label="Booking" sx={styles.nestedMenuItem} parentMenuOpen={isSubMenuOpen}>
        {<Box sx={styles.menuItemBox}>
          <ContextMenuItem onClick={handleMakeBooking} disabled={!canMakeBooking} label="Create Booking..." />
        </Box>}
      </NestedMenuItem>
      <Divider />
      <NestedMenuItem label="Shift" sx={styles.nestedMenuItem} parentMenuOpen={isSubMenuOpen}>
        {<Box sx={styles.menuItemBox}>
          <ContextMenuItem onClick={() => handleShiftBlockStatusUpdate(true)} disabled={isShiftComplete} label="Block Shift" />
          <Divider />
          <ContextMenuItem onClick={() => handleShiftBlockStatusUpdate(false)} disabled={isShiftComplete} label="Unblock Shift" />
          <Divider />
          <ContextMenuItem onClick={handleEditShift} disabled={isShiftComplete} label="Edit Shift" />
          <Divider />
          <ContextMenuItem onClick={handleClearSessions} disabled={!canClearSessions} label="Clear Sessions" />
          <Divider />
          <ContextMenuItem onClick={handleDeleteShift} disabled={canDeleteShift} label="Delete Shift" />
        </Box>}
      </NestedMenuItem>
    </Menu>
  );
};

export default TimeslotContextMenu;